import { X, Heart, ShoppingBag, Star, Minus, Plus, AlertTriangle, RotateCw } from "lucide-react";
import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import type { Product } from "@/lib/products";
import { useCart } from "@/lib/store";

type Props = {
  product: Product | null;
  wishlisted: boolean;
  loading?: boolean;
  error?: string | null;
  onWishlist: () => void;
  onClose: () => void;
  onRetry?: () => void;
};

export function QuickView({ product, wishlisted, loading = false, error = null, onWishlist, onClose, onRetry }: Props) {
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const router = useRouter();
  const open = loading || !!error || !!product;
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-8" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] bg-card rounded-t-[24px] sm:rounded-[24px] overflow-y-auto shadow-luxe-hover grid grid-cols-1 md:grid-cols-2 animate-in slide-in-from-bottom-4 sm:zoom-in-95 fade-in duration-300 sm:min-h-[420px]">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 h-11 w-11 sm:h-10 sm:w-10 rounded-full bg-cream/95 grid place-items-center hover:bg-gold transition-colors shadow-luxe">
          <X className="h-4 w-4" />
        </button>

        {loading ? (
          <QuickViewSkeleton />
        ) : error ? (
          <QuickViewError message={error} onRetry={onRetry} onClose={onClose} />
        ) : product ? (
          <>
        <div className="h-[42vh] max-h-[340px] md:h-full md:max-h-none md:aspect-auto bg-secondary overflow-hidden">
          <img src={product.img} alt={product.name} className="h-full w-full object-cover" />
        </div>

        <div className="p-6 sm:p-8 md:p-10 flex flex-col">
          <div className="eyebrow">{product.category} Collection</div>
          <h3 className="font-display text-2xl sm:text-3xl md:text-4xl mt-3">{product.name}</h3>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex text-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-current" : "opacity-30"}`} strokeWidth={0} />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{product.rating} · {product.reviews} reviews</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-display text-3xl">₹{product.price}</span>
            {product.compareAt && <span className="text-base text-muted-foreground line-through">₹{product.compareAt}</span>}
          </div>

          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
            A signature Royal Lumina pour — {product.fragrance.toLowerCase()} notes cured slowly in small batches
            for a luxurious, even burn that transforms any room into a quiet ritual.
          </p>

          <dl className="mt-6 grid grid-cols-2 gap-3 text-xs">
            <Meta k="Fragrance" v={product.fragrance} />
            <Meta k="Wax" v="Natural Soy" />
            <Meta k="Burn Time" v="Up to 60 hrs" />
            <Meta k="Weight" v="220 g" />
          </dl>

          <div className="mt-7 flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center rounded-[14px] border border-border h-12 shrink-0">
              <button aria-label="Decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-11 grid place-items-center hover:text-gold active:bg-secondary"><Minus className="h-4 w-4" /></button>
              <div className="w-8 text-center font-medium">{qty}</div>
              <button aria-label="Increase" onClick={() => setQty((q) => q + 1)} className="h-12 w-11 grid place-items-center hover:text-gold active:bg-secondary"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              disabled={!product.inStock}
              onClick={() => { if (add(product.id, qty)) { onClose(); router.navigate({ to: "/cart" }); } }}
              className="btn-gold flex-1 min-w-[140px] justify-center h-12 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ShoppingBag className="h-4 w-4" /> {product.inStock ? "Add to Cart" : "Notify Me"}
            </button>
            <button
              onClick={onWishlist}
              aria-label="Wishlist"
              className={`h-12 w-12 shrink-0 rounded-[14px] border grid place-items-center transition-colors ${wishlisted ? "bg-gold border-gold text-primary" : "border-border hover:border-gold"}`}
            >
              <Heart className="h-4 w-4" fill={wishlisted ? "currentColor" : "none"} strokeWidth={1.6} />
            </button>
          </div>

          <div className="mt-6 text-xs text-muted-foreground tracking-[0.15em] uppercase">
            {product.inStock ? "★ In Stock · Ships in 24 hrs" : "Currently Sold Out"}
          </div>
        </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function QuickViewSkeleton() {
  return (
    <>
      <div className="relative aspect-square md:aspect-auto md:h-full bg-secondary overflow-hidden">
        <div className="absolute inset-0 shimmer" />
      </div>
      <div className="p-8 md:p-10 flex flex-col gap-4">
        <div className="h-2.5 w-32 rounded-full bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
        <div className="h-8 w-3/4 rounded-md bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
        <div className="h-3 w-40 rounded-full bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
        <div className="h-7 w-28 rounded-md bg-secondary relative overflow-hidden mt-2"><div className="absolute inset-0 shimmer" /></div>
        <div className="space-y-2 mt-2">
          <div className="h-3 w-full rounded-full bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
          <div className="h-3 w-11/12 rounded-full bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
          <div className="h-3 w-4/5 rounded-full bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 rounded-[10px] bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
          ))}
        </div>
        <div className="mt-auto pt-6 flex items-center gap-4">
          <div className="h-12 w-28 rounded-[14px] bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
          <div className="h-12 flex-1 rounded-[14px] bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
          <div className="h-12 w-12 rounded-[14px] bg-secondary relative overflow-hidden"><div className="absolute inset-0 shimmer" /></div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
          Preparing your candle…
        </div>
      </div>
    </>
  );
}

function QuickViewError({ message, onRetry, onClose }: { message: string; onRetry?: () => void; onClose: () => void }) {
  return (
    <div className="md:col-span-2 p-10 md:p-14 flex flex-col items-center justify-center text-center min-h-[420px]">
      <div className="h-16 w-16 rounded-full bg-secondary grid place-items-center">
        <AlertTriangle className="h-7 w-7 text-gold" strokeWidth={1.4} />
      </div>
      <div className="mt-6 eyebrow">A quiet interruption</div>
      <h3 className="mt-3 font-display text-3xl md:text-4xl">
        We couldn't <span className="italic text-gold">reveal this piece</span>
      </h3>
      <p className="mt-3 max-w-md text-sm text-muted-foreground leading-relaxed">
        {message || "Something disturbed the flame while loading this candle. Please try again in a moment."}
      </p>
      <div className="mt-8 flex items-center gap-3">
        {onRetry && (
          <button onClick={onRetry} className="btn-gold h-11">
            <RotateCw className="h-4 w-4" /> Try again
          </button>
        )}
        <button onClick={onClose} className="h-11 px-6 rounded-[14px] border border-border text-sm tracking-[0.15em] uppercase text-primary hover:border-gold transition-colors">
          Close
        </button>
      </div>
    </div>
  );
}

function Meta({ k, v }: { k: string; v: string }) {
  return (
    <div className="border border-border rounded-[10px] p-3">
      <dt className="tracking-[0.2em] uppercase text-muted-foreground">{k}</dt>
      <dd className="mt-1 text-primary font-medium">{v}</dd>
    </div>
  );
}
